import IconButton from '@/components/common/icon-button';
import { FloorGuidePoiContentCarousel } from '@/components/feature/floor-guide/floor-guide-poi-content-carousel';
import CloseIcon from '@/components/icons/CloseIcon';
import shopStyles from '@/components/feature/shop/shop-poi-modal/styles.module.scss';
import { useScaleSize } from '@/hooks';
import { useFocusTrap } from '@/hooks/modal';
import type { PoiContent } from '@/services/dto';
import { useConfigStore, useModalStackStore } from '@/stores';
import type { PoiContentSlide } from '@/types/shop';
import { formatPoiSlideToDetail } from '@/utils/floor-guide/formatPoiSlideToDetail';
import clsx from 'clsx';
import { useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { buildFloorGuidePoiDetailTabOrders } from './buildTabOrders';
import modalStyles from './horizontal-poi-modal.module.scss';
import { FloorGuidePoiDetailHorizontal } from './poi-detail-horizontal';

type Props = {
    isOpen: boolean;
    language: string;
    poiContent: PoiContentSlide | null;
    relatedPoiContents?: PoiContent[];
    selectedPoiContentId?: string;
    onSelectPoiContent?: (poi: PoiContent) => void;
    onClose: () => void;
    onGuideStart: () => void;
};

export const FloorGuideHorizontalPoiModal = ({
    isOpen,
    language,
    poiContent,
    relatedPoiContents = [],
    selectedPoiContentId,
    onSelectPoiContent,
    onClose,
    onGuideStart,
}: Props) => {
    const { t } = useTranslation();
    const contrast = useConfigStore((s) => s.config?.ui?.contrast);
    const { scaleWidth, scaleHeight } = useScaleSize();
    const { pushModal, popModal, isModalInStack } = useModalStackStore();
    const modalId = useId();
    const modalRef = useRef<HTMLDivElement>(null);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);

    const poiDetail = useMemo(() => formatPoiSlideToDetail(poiContent, language), [poiContent, language]);
    const showContentCarousel = relatedPoiContents.length > 1;
    const carouselCount = showContentCarousel ? relatedPoiContents.length : 0;
    const tabOrders = useMemo(
        () => (poiDetail ? buildFloorGuidePoiDetailTabOrders(poiDetail, { carouselCount }) : null),
        [poiDetail, carouselCount]
    );

    const modalSurfaceClassName = clsx(
        shopStyles['poi-modal'],
        shopStyles['poi-modal__horizontal'],
        modalStyles['floor-guide-horizontal-poi-modal__surface'],
        {
            [shopStyles['poi-modal__contrast']]: contrast,
        }
    );

    useEffect(() => {
        if (isOpen) {
            if (!isModalInStack(modalId)) {
                pushModal(modalId);
            }
        } else if (isModalInStack(modalId)) {
            popModal(modalId);
        }

        return () => {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        };
    }, [isOpen, modalId, pushModal, popModal, isModalInStack]);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        if (prevActiveElementRef.current) {
            const elementToRestore = prevActiveElementRef.current;
            prevActiveElementRef.current = null;
            requestAnimationFrame(() => {
                if (document.body.contains(elementToRestore)) {
                    elementToRestore.focus({ preventScroll: false });
                }
            });
        }
        popModal(modalId);
        onClose();
    }, [modalId, onClose, popModal]);

    useFocusTrap(modalRef, isOpen, handleClose, false, modalId);

    if (!poiDetail || !tabOrders || !isOpen) {
        return null;
    }

    const detailBody = (
        <FloorGuidePoiDetailHorizontal
            poiDetail={poiDetail}
            language={language}
            onGuideStart={onGuideStart}
            carouselCount={carouselCount}
        />
    );

    const closeButton = (
        <IconButton
            role="button"
            onClick={handleClose}
            ariaLabel={t('common.dialog.closeModal')}
            icon={<CloseIcon color="white" width={19} height={19} />}
            width={scaleWidth(80)}
            height={scaleHeight(80)}
            tabIndex={tabOrders.close}
            data-playsound="ting"
            data-interactive
            data-modal-region
            data-modal-region-order={tabOrders.close}
        />
    );

    return (
        <div className={modalStyles['floor-guide-horizontal-poi-modal__overlay']} style={{ zIndex: 60 }}>
            <div
                className={clsx(showContentCarousel && modalStyles['floor-guide-horizontal-poi-modal__stack'])}
                role="dialog"
                aria-modal="true"
                ref={modalRef}
            >
                <div className={modalStyles['floor-guide-horizontal-poi-modal__detail-wrap']}>
                    <div className={modalSurfaceClassName}>{detailBody}</div>
                    {!showContentCarousel && (
                        <div className={modalStyles['floor-guide-horizontal-poi-modal__close']}>{closeButton}</div>
                    )}
                </div>
                {showContentCarousel && (
                    <div className={modalStyles['floor-guide-horizontal-poi-modal__carousel-wrap']}>
                        <FloorGuidePoiContentCarousel
                            poiList={relatedPoiContents}
                            selectedPoiId={selectedPoiContentId}
                            onSelectPoi={onSelectPoiContent}
                            tabOrders={tabOrders.carousel}
                        />
                    </div>
                )}
                {showContentCarousel && (
                    <div
                        className={clsx(
                            modalStyles['floor-guide-horizontal-poi-modal__close'],
                            modalStyles['floor-guide-horizontal-poi-modal__close--below-list']
                        )}
                    >
                        {closeButton}
                    </div>
                )}
            </div>
        </div>
    );
};
